import axios from 'axios';

import { apiBaseUrl } from './apiBaseUrl';

/**
 * Liveness state of the gateway API, as seen from the browser.
 *
 * The CRM never polls a dedicated health endpoint while everything is fine: the state is derived
 * from the API calls the app already makes (`src/utils/axios.ts` response interceptor and the
 * boot-time compliance check). A probe is only fired to confirm a suspected outage, and then on a
 * back-off loop while the API is considered down, so a deploy of a few minutes is detected and
 * recovered from without a permanent heartbeat.
 */
export type BackendStatus = 'up' | 'down';

type Listener = (status: BackendStatus) => void;

/**
 * Probe endpoint: the public (permitAll) country-by-IP compliance check the app already calls at
 * boot. It is cheap, requires no token, and answers even when the user is not logged in.
 */
const PROBE_URL = `${apiBaseUrl}compliance/countries/ip/check`;
const PROBE_TIMEOUT_MS = 8_000;
const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 30_000;

/** Statuses a load balancer returns while the backend behind it is restarting. */
const GATEWAY_DOWN_STATUSES = new Set([502, 503, 504]);

// Bare client: no interceptors, so a probe can never re-enter the failure reporting below.
const probeClient = axios.create({ timeout: PROBE_TIMEOUT_MS });

let status: BackendStatus = 'up';
let listeners: Listener[] = [];
let probeInFlight: Promise<boolean> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = RETRY_MIN_MS;

/**
 * Tells whether an error means "the API did not answer" rather than "the API answered with an
 * error". Only the former is an outage: a 4xx, or any non-axios error, proves the API is alive.
 */
export const isUnreachableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.code === 'ERR_CANCELED') return false;

  const httpStatus = error.response?.status;
  // No response at all: DNS failure, connection refused, TLS error, timeout, blocked CORS preflight.
  if (httpStatus === undefined) return true;

  return GATEWAY_DOWN_STATUSES.has(httpStatus);
};

/** The browser knowing it is offline is a local problem, not a platform outage. */
const isBrowserOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

const emit = () => {
  listeners.forEach((listener) => listener(status));
};

export const getBackendStatus = (): BackendStatus => status;

export const subscribeBackendStatus = (listener: Listener): (() => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((registered) => registered !== listener);
  };
};

const stopRetryLoop = () => {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  retryDelay = RETRY_MIN_MS;
};

/**
 * Spreads the retry of every open tab over the delay window. Without it, a deploy ending would
 * have all the clients probing — then reloading — on the very same second.
 */
const withJitter = (delay: number): number => Math.round(delay * (0.7 + Math.random() * 0.6));

const scheduleRetry = () => {
  if (retryTimer) return;

  retryTimer = setTimeout(async () => {
    retryTimer = null;
    const alive = await probeBackend();
    if (alive) {
      setStatus('up');
      return;
    }
    retryDelay = Math.min(Math.round(retryDelay * 1.5), RETRY_MAX_MS);
    scheduleRetry();
  }, withJitter(retryDelay));
};

const setStatus = (next: BackendStatus) => {
  if (next === status) return;
  status = next;

  if (next === 'down') {
    scheduleRetry();
  } else {
    stopRetryLoop();
  }

  emit();
};

/**
 * Calls the probe endpoint once and resolves to whether the API answered.
 * Concurrent callers share the same in-flight request.
 */
export const probeBackend = (): Promise<boolean> => {
  if (probeInFlight) return probeInFlight;

  const run = probeClient.get(PROBE_URL).then(
    () => true,
    (error) => !isUnreachableError(error),
  );

  probeInFlight = run.finally(() => {
    probeInFlight = null;
  });

  return probeInFlight;
};

/** Marks the API as reachable. Called on every successful response of the app's axios instance. */
export const markBackendUp = () => setStatus('up');

/** Marks the API as unreachable without re-probing, for callers that already saw the failure. */
export const markBackendDown = () => {
  if (isBrowserOffline()) return;
  setStatus('down');
};

/**
 * Reports a failed API call. A single failure is not enough to blank the app, so the outage is
 * confirmed with one probe before the maintenance screen is shown.
 */
export const reportApiFailure = async (error: unknown): Promise<void> => {
  if (status === 'down') return;
  if (!isUnreachableError(error)) return;
  if (isBrowserOffline()) return;

  const alive = await probeBackend();
  if (!alive) setStatus('down');
};

/** Immediate check, for the "retry now" button of the maintenance screen. */
export const checkBackendNow = async (): Promise<BackendStatus> => {
  const alive = await probeBackend();
  setStatus(alive ? 'up' : 'down');
  return status;
};

if (typeof window !== 'undefined') {
  // Coming back from a laptop sleep or a lost connection: check straight away instead of waiting
  // for the back-off timer.
  window.addEventListener('online', () => {
    if (status === 'down') void checkBackendNow();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && status === 'down') {
      void checkBackendNow();
    }
  });
}
